import React, { useState, useRef, useEffect, useMemo } from "react";
import { useCart } from "../context/CartContext";
import "./ExtrasModal.css";

export default function ExtrasModal({ dish, onClose, maxAcompanamientos = 3 }) {
  const { dispatch } = useCart();
  const submittedRef = useRef(false);

  const [extrasSelected, setExtrasSelected] = useState([]);
  const [acomSelected, setAcomSelected] = useState([]);
  const [note, setNote] = useState("");

  // ✅ ahora vienen aquí
  const complementos = Array.isArray(dish.complementos)
    ? dish.complementos
    : [];

  // Detecta si una categoría es "acompañamientos"
  const isAcomCategory = (cat) =>
    String(cat || "")
      .toLowerCase()
      .includes("acom"); // "acompañamientos", "acompanamientos", etc.

  // ✅ Agrupar por categoria
  const complementosByCategoria = useMemo(() => {
    return complementos.reduce((acc, item) => {
      const cat = item.categoria || "Otros";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
  }, [complementos]);

  const hasComplementos = complementos.length > 0;

  // ----- ACOMPAÑAMIENTOS (máx. 3) -----
  const toggleAcompanamiento = (acom) => {
    const exists = acomSelected.some((a) => a.id === acom.id);

    if (exists) {
      setAcomSelected(acomSelected.filter((a) => a.id !== acom.id));
      return;
    }

    if (acomSelected.length >= maxAcompanamientos) {
      alert(
        `Solo puedes elegir máximo ${maxAcompanamientos} acompañamiento${maxAcompanamientos > 1 ? "s" : ""}.`,
      );
      return;
    }

    setAcomSelected([...acomSelected, acom]);
  };

  // ----- Salsas (máx. 1) -----
  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const isExtrasCategory = (cat) => norm(cat).includes("extra"); // Extras (o "adiciones" si quieres)

  const isSingleCategory = (cat) =>
    !isAcomCategory(cat) && !isExtrasCategory(cat);

  // ----- EXTRAS -----
  const toggleExtra = (item) => {
    setExtrasSelected((prev) => {
      const exists = prev.some((e) => e.id === item.id);
      const cat = item.categoria || "Otros";

      // ✅ Categorías diferentes de Extras y Acompañamientos: SOLO 1
      if (isSingleCategory(cat)) {
        if (exists) return prev.filter((e) => e.id !== item.id); // desmarca
        // marca y quita cualquier otro de ESA misma categoría
        return [...prev.filter((e) => (e.categoria || "Otros") !== cat), item];
      }

      // ✅ Extras: multi normal
      if (exists) return prev.filter((e) => e.id !== item.id);
      return [...prev, item];
    });
  };

  // ✅ toggle universal (según categoria)
  const toggleComplemento = (item) => {
    if (isAcomCategory(item.categoria)) toggleAcompanamiento(item);
    else toggleExtra(item);
  };

  // ✅ checked universal (según categoria)
  const isChecked = (item) => {
    if (isAcomCategory(item.categoria)) {
      return acomSelected.some((a) => a.id === item.id);
    }
    return extrasSelected.some((e) => e.id === item.id);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const nav = document.querySelector(".bottom-nav");
    if (nav) nav.style.display = "none";

    return () => {
      document.body.style.overflow = "auto";
      if (nav) nav.style.display = "flex";
    };
  }, []);

  const addItem = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    // Unimos todo lo seleccionado
    const allSelected = [
      ...acomSelected.map((i) => ({
        ...i,
        categoria: i.categoria || "Acompañamientos",
      })),
      ...extrasSelected,
    ];

    // Agrupamos por categoría
    const complementosSeleccionadosPorCategoria = allSelected.reduce(
      (acc, item) => {
        const cat = item.categoria || "Otros";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
      },
      {},
    );

    dispatch({
      type: "ADD_ITEM",
      payload: {
        dishId: dish.id,
        name: dish.name,
        namecor: dish.namecor,
        price: dish.price,

        // ✅ NUEVO: complementos por categoria
        complementos: complementosSeleccionadosPorCategoria,

        // (Opcional) deja esto si tu carrito/reducer aún lo usa
        extras: extrasSelected,
        acompanamientos: acomSelected,

        note,
      },
    });

    onClose();
  };

  return (
    <div className="extras-overlay">
      <div className="extras-box">
        <h2 className="modal-title">Personalizar tu pedido</h2>

        {/* ✅ COMPLEMENTOS POR CATEGORIA */}
        {hasComplementos && (
          <>
            {Object.entries(complementosByCategoria).map(
              ([categoria, items]) => (
                <React.Fragment key={categoria}>
                  <h3 className="section-title">
                    {isAcomCategory(categoria)
                      ? `Acompañamientos (máx. ${maxAcompanamientos})`
                      : categoria}
                  </h3>

                  <div className="item-list">
                    {items.map((item) => {
                      const sinStock = Number(item.stock) <= 0;
                      return (
                        <label
                          key={item.id}
                          className={`extra-row ${sinStock ? "extra-row-disabled" : ""}`}
                        >
                          <div className="extra-left">
                            {item.name}

                            {sinStock && (
                              <span className="sin-stock-text">Sin stock</span>
                            )}
                          </div>

                          {/* Si NO es acompañamiento, muestra precio (si aplica) */}
                          {!isAcomCategory(categoria) &&
                            (Number(item.price) || 0) > 0 && (
                              <div className="extra-price">
                                $
                                {new Intl.NumberFormat("es-CO").format(
                                  Number(item.price),
                                )}
                              </div>
                            )}

                          <input
                            type="checkbox"
                            checked={isChecked(item)}
                            disabled={sinStock}
                            onChange={() =>
                              !sinStock && toggleComplemento(item)
                            }
                          />
                        </label>
                      );
                    })}
                  </div>
                </React.Fragment>
              ),
            )}
          </>
        )}

        {/* NOTA */}
        <textarea
          className="note-input"
          placeholder="Nota para la cocina (opcional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {/* BOTONES */}
        <div className="modal-actions">
          <button className="confirm-btn" onClick={addItem}>
            Agregar al carrito
          </button>

          <button className="close-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
